import React from "react"; // 169 — react/react-in-jsx-scope is counted per JSX element; the rule's own remedy
import FreeDateInput from "../components/FreeDateInput"; // 213B §15 — the free window's edges
import { useState, useMemo, useEffect, useRef } from "react";
import { foreignOf, sumForeign } from "../components/MoneyStack"; // 169 R7
import { RECAP_MEMBERS, dueTone, columnGateOf, listColumnShown } from "../lib/matterListCfg"; // 191 F.0 — the case list's seven; fold-in 2 — its column switches
import { BASIS_BAR_BORDER } from "../components/CountersStrip"; // 191 F.0 — the bar on Envoyé
import {
  Search,
  FileText,
  FileType,
  Sheet,
  Scale,
  Users,
  Calendar,
  UserCheck,
  Contact,
  UserX,
  Gavel,
  Microscope,
  Landmark,
  UsersRound,
  CalendarDays,
  ReceiptEuro,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Clock,
  FileWarning,
  Mail,
  Filter, // 182 B.3 — the funnel tab
} from "lucide-react";
import { I, SK, fc, fq, fmin, fd, pname } from "../utils";
import { resolveMatter } from "../lib/matterLabel"; // 191 C.2b
import { api, computeReportsAggregates } from "../api";
import {
  redRepMatter,
  redRepEntry,
  redRepCo,
  redRepClient,
  redRepInv,
  redRoleRows,
} from "../lib/financialsClient";
import { useRoleLabels } from "../hooks/useRoleLabels";
import { SegmentedControl, SortCaret } from "../components/SharedUI"; // 200-glance §M — the one sort caret
import { periodBounds, monthBounds, PERIOD_OPTIONS } from "../lib/periodRange";
import type { PeriodKind } from "../lib/periodRange";
import MenuSelect from "../components/MenuSelect"; // 174 §3d — nine options is a select, not a pill bar
import {
  matterPassesProspect,
  prospectFilterCounts,
  prospectFilterOptions,
  type ProspectFilterValue,
  type ProspectTaggable,
} from "../lib/prospectFilter"; // 139 §3
import { usePermissions } from "../hooks/usePermissions";
import PermissionsReportPanel from "../components/PermissionsReportPanel";
// 182 B.3 — the prospect funnel, a stand-alone panel like the permissions one.
import FunnelReportPanel from "../components/FunnelReportPanel";
import { useTranslation } from "../i18n";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import { asThemeVars } from "../lib/themeDerivation"; // 203-glance §C.1 — the day theme prop's seam
import { normaliseAppFont } from "../lib/appFonts"; // 203-glance-2 §C — the exporting user's face, narrowed at the seam
import { printTable, exportWord, exportExcel, fdE, type ExportDress } from "../lib/tableExport"; // 203 A.1 — the three exporters (and the date they print), one module for two surfaces; A.2.0 — the print path takes the table, not its markup; 203-glance §C — the dress
import { RAD, FS } from "../lib/tokens";
import { invoiceStatusWord } from "../lib/invoiceStatusWord"; // 209 §16 — a report row prints one document's status in words
import type { TFn } from "../lib/taxonomyTypes";
import Table from "../components/Table";
import { storedClientLanguage } from "../lib/docLang.mjs"; // 207C §6 — a client's language as stored

// Phase M+2b — each tab declares its gate. Financial tabs surface monetary
// aggregations (fees, billed/unbilled, invoiced/paid); operational tabs are
// directory-style (counts, contacts, agenda density). Gate via
// lateral.reportsFinancial vs lateral.reportsOperational.
// Stage 3: each tab carries an i18n key in addition to the English label, so the
// label can flow through t() at render time. The literal `l` stays as the EN
// fallback (referenced via `labelKey` lookup at render).
// P21-zero — `surface` names the lateral DIRECTORY a tile reports on, for the
// tiles that are about one (sectionMeta.SURFACE_SECTION_KEY resolves it to a
// section key). It is a second, orthogonal gate to `gate`: `gate` asks may this
// viewer see reports of this family, `surface` asks does this firm have the
// thing being reported on. Before it, only the family was asked — so hiding
// jurisdictions left a "Jurisdictions" tile reporting on a directory the
// product no longer had. Tiles with no `surface` are roll-ups over cases and
// money rather than over a directory, and keep the family gate alone.
const TABS = [
  {
    k: "case",
    l: "By Case",
    labelKey: "reports.tab.case",
    i: Scale,
    gate: "financial",
  },
  {
    k: "collab",
    l: "By Collaborator",
    labelKey: "reports.tab.collab",
    i: Users,
    gate: "financial",
  },
  {
    k: "period",
    l: "By Period",
    labelKey: "reports.tab.period",
    i: Calendar,
    gate: "financial",
  },
  {
    k: "client",
    l: "By Client",
    labelKey: "reports.tab.client",
    i: UserCheck,
    gate: "financial",
  },
  {
    k: "clients",
    l: "Clients",
    labelKey: "reports.tab.clients",
    surface: "cl",
    i: Contact,
    gate: "operational",
  },
  {
    k: "opponents",
    l: "Opponents",
    labelKey: "reports.tab.opponents",
    surface: "op",
    i: UserX,
    gate: "operational",
  },
  {
    k: "opplaw",
    l: "Opp. Lawyers",
    labelKey: "reports.tab.opplaw",
    surface: "ocu",
    i: Gavel,
    gate: "operational",
  },
  {
    k: "experts",
    l: "Experts",
    labelKey: "reports.tab.experts",
    surface: "ex",
    i: Microscope,
    gate: "operational",
  },
  {
    k: "jurisdictions",
    l: "Jurisdictions",
    labelKey: "reports.tab.jurisdictions",
    surface: "ju",
    i: Landmark,
    gate: "operational",
  },
  {
    k: "collabs",
    l: "Collaborators",
    labelKey: "reports.tab.collabs",
    surface: "co2int",
    i: UsersRound,
    gate: "financial",
  },
  {
    k: "agenda",
    l: "Agenda",
    labelKey: "reports.tab.agenda",
    surface: "ag",
    i: CalendarDays,
    gate: "operational",
  },
  {
    k: "correspondence",
    l: "Correspondence",
    labelKey: "reports.tab.correspondence",
    i: Mail,
    gate: "operational",
  },
  {
    k: "invoices",
    l: "Invoices",
    labelKey: "reports.tab.invoices",
    surface: "invoices",
    i: ReceiptEuro,
    gate: "financial",
  },
  {
    k: "permissions",
    l: "Permissions",
    labelKey: "reports.tab.permissions",
    i: Users,
    gate: "operational",
  },
  // 182 B.3 — the prospect funnel: rows per lead source over a from/to window.
  // Operational (it reports on the persons directory), so `surface: "cl"`.
  {
    k: "funnel",
    l: "Funnel",
    labelKey: "reports.tab.funnel",
    surface: "cl",
    i: Filter,
    gate: "operational",
  },
  // Phase 3 C5 — CDD report sub-tabs (gate: cdd, lateral.reportsCDD).
  {
    k: "cddHealth",
    l: "CDD Health",
    labelKey: "cdd.reports.compliance_health",
    i: ShieldCheck,
    gate: "cdd",
  },
  {
    k: "cddTier",
    l: "Tier Distribution",
    labelKey: "cdd.reports.tier_distribution",
    i: ShieldAlert,
    gate: "cdd",
  },
  {
    k: "cddExpiring",
    l: "Expiring",
    labelKey: "cdd.reports.expiring",
    i: Clock,
    gate: "cdd",
  },
  {
    k: "cddPep",
    l: "PEP Register",
    labelKey: "cdd.reports.pep_register",
    i: AlertTriangle,
    gate: "cdd",
  },
  {
    k: "cddSar",
    l: "SAR Log",
    labelKey: "cdd.reports.sar_log",
    i: FileWarning,
    gate: "cdd",
  },
  {
    k: "cddAtypical",
    l: "Atypical Ops",
    labelKey: "cdd.reports.atypical_log",
    i: AlertTriangle,
    gate: "cdd",
  },
];

// C.2 — the tabs whose rows are server-computed (POST /api/financials/
// reports). Their PDF/Word/Excel exports are guarded until the first
// response lands — exporting the pre-response emptiness would read as
// "no data" (same memo/export guard convention as the interest memo and
// the retainer XLSX).
const MONEY_TAB_KEYS = new Set([
  "case",
  "collab",
  "period",
  "client",
  "collabs",
  "invoices",
]);

const eurN = (n: any) =>
  new Intl.NumberFormat("fr-BE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0);
// 169 R7 — a DOCUMENT figure with its other currencies: the home string exactly
// as `eurN` printed it, then " · " and one "amount CODE" per other currency.
// TEXT, on purpose: the print and Word exports interpolate cells and sums as
// strings (recordings/04e-html-escaping pins those lines of this file), and
// the screen stacks the same text through `renderMoney` below — MoneyStack's
// rule (home line, muted lines at FS.s10) applied to a string.
const MONEY_SEP = " · ";
const moneyStack = (home: number, foreign: Record<string, number> | null) =>
  foreign
    ? [eurN(home), ...Object.entries(foreign).map(([code, v]) => `${eurN(v)} ${code}`)].join(MONEY_SEP)
    : eurN(home);
const FOREIGN_LINE = /^[\d\s.,\u00a0\u202f-]+ [A-Z]{3}$/;
const renderMoney = (cell: unknown): React.ReactNode => {
  if (typeof cell !== "string" || !cell.includes(MONEY_SEP)) return cell as React.ReactNode;
  const [home, ...rest] = cell.split(MONEY_SEP);
  if (!rest.length || !rest.every((x) => FOREIGN_LINE.test(x))) return cell;
  return (
    <>
      {home}
      {rest.map((line) => (
        <div key={line} style={{ fontSize: FS.s10, color: "var(--tm)", fontWeight: 400 }}>
          {line}
        </div>
      ))}
    </>
  );
};
// ============ COMPONENT ============

const ReportsView = ({
  ca,
  cc,
  cl,
  co,
  op,
  ocu,
  ex,
  ju,
  te,
  ag,
  tk,
  inv,
  currentUser,
  tkTypes,
  evtTypes,
  nav,
  firmSettings,
  dayTheme: _dayThemeAny, // 203-glance §C.1 — the firm's DAY theme's vars (App's dayThemeVarsOf), for the documents' palette
  appFont: _appFontAny, // 203-glance-2 §C — the exporting user's text face (App's appFont), for the documents
}: any) => {
  // 203-glance §C.1 — the day theme, narrowed once at the seam
  const dayTheme = asThemeVars(_dayThemeAny);
  // 203-glance-2 §C — the face, narrowed once at the seam: `normaliseAppFont` takes unknown and answers a family this build renders
  const appFont = normaliseAppFont(_appFontAny);
  // Hook variable named `tr` to avoid clashing with the closure variable `t`
  // (tab) used inside the visibleTabs.map callbacks below.
  const { t: tr } = useTranslation();
  // 209 §16 — one document's status in words, for the invoice rows: typed once
  // here, where the row's `any` and the context's `any` meet the helper.
  const statusWord = (s: unknown): string => invoiceStatusWord(typeof s === "string" ? s : null, tr as TFn);
  // Phase M+3.7 Stage 3 round 3 — raw taxonomy entries to localise case
  // type / status badges and filter options. Reuses the module-level cache
  // populated elsewhere; just re-renders this view when language flips.
  const _caseStatusEntries = useTaxonomy("case_status").entries;
  const _caseTypeEntries = useTaxonomy("case_type").entries;
  const firmName = firmSettings?.firm?.name || "Legal Practice Manager";
  // Phase M+2b — per-tab gating. Admin sees all; non-admins see only the
  // tabs whose gate they can pass. App.jsx nav already gates reachability
  // at OR-of-both-gates, so a viewer with only one gate still arrives here.
  // P20f — `hidden(k) &&` guards the admin short-circuit below: a section the
  // firm's product does not have is not a permission an admin overrides, and
  // `isAdmin ||` would otherwise skip can() (which already denies it). The CDD
  // tab is the live case — lateral.reportsCDD is module-bound to 'cdd'.
  const { can, hidden, hiddenSurface, viewer } = usePermissions();
  const isAdmin = !!viewer?.isAdmin;
  const canFinancial = !hidden("lateral.reportsFinancial") && (isAdmin || can("lateral.reportsFinancial"));
  const canOperational = !hidden("lateral.reportsOperational") && (isAdmin || can("lateral.reportsOperational"));
  const canCdd = !hidden("lateral.reportsCDD") && (isAdmin || can("lateral.reportsCDD"));
  const visibleTabs = useMemo(
    () =>
      TABS.filter((t) =>
        // P21-zero — the reported-on directory first: a tile for something
        // this firm's product does not have is the same dead affordance as a
        // sidebar row for it, and the family gate above cannot see that.
        (t as any).surface && hiddenSurface((t as any).surface)
          ? false
          : t.gate === "financial"
            ? canFinancial
            : t.gate === "operational"
              ? canOperational
              : t.gate === "cdd"
                ? canCdd
                : false,
      ),
    [canFinancial, canOperational, canCdd, hiddenSurface],
  );
  // Phase 3 C5 — fetch the aggregated CDD report payload once when any CDD
  // tab becomes active. Cached in state for the session. State declarations
  // must precede the useEffect AND `tab` must already be declared by the
  // time we reference it in the deps array (Stage 3 fix #3 — was a TDZ
  // "Cannot access 'Le' before initialization" error in production).
  const [cddReport, setCddReport] = useState<any>(null);
  const [cddLoading, setCddLoading] = useState(false);
  const [tab, setTab] = useState(() => visibleTabs[0]?.k || "case");
  useEffect(() => {
    if (!tab || !tab.startsWith("cdd") || cddReport || cddLoading) return;
    setCddLoading(true);
    api
      .get("/reports/cdd")
      .then((d) => setCddReport(d))
      .catch((e) => console.warn("[reports.cdd] load failed", e))
      .finally(() => setCddLoading(false));
  }, [tab, cddReport, cddLoading]);
  // If permissions resolve after first render and the active tab is no
  // longer visible, snap to the first visible tab.
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (!visibleTabs.some((t) => t.k === tab)) setTab(visibleTabs[0].k);
  }, [visibleTabs, tab]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ k: null, d: 1 });
  const [filt, setFilt] = useState<Record<string, any>>({});
  const gf = (k: any, def?: any) => filt[k] || def || "all";
  const sf = (k: any, v: any) => setFilt((p) => ({ ...p, [k]: v }));

  // Date helpers for period filters.
  //
  // 150 §3 — THE MONTH AND QUARTER BOUNDARIES WERE OFF BY ONE, ALL DAY LONG,
  // and now come from lib/periodRange (the one copy the te-filter reads too).
  // What stood here built a LOCAL midnight and printed it via toISOString(),
  // which converts to UTC first — so in Brussels `mon1` read the LAST DAY OF
  // THE PREVIOUS MONTH and `monLast`/`thisQuarterEnd` read one day short, at
  // every hour of the day (a midnight-anchored Date is always behind UTC by
  // the whole offset). The By-Collaborator month window and the agenda month
  // filter both consumed the shifted pair.
  //
  // `todayStr`/`weekStart`/`d15`/`d30` keep the tree-wide `toISOString`
  // stamp-the-current-moment idiom: those carry the current TIME, so they
  // shift only in the small hours (00:00–02:00 local), the app's standing
  // posture — not this defect's class, not changed here.
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const thisYear = String(now.getFullYear());
  const lastYear = String(now.getFullYear() - 1);
  /* 174 §3d — `thisMonth`, the quarter pair and the year pair went with the
     three-option collab filter that read them; `periodBounds` computes every
     window from the kind now. `mon1`/`monLast` below stay: the agenda month
     filter (:1283) still reads them. */
  const monD = new Date(now);
  monD.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const sunD = new Date(monD);
  sunD.setDate(monD.getDate() + 6);
  const weekStart = monD.toISOString().slice(0, 10);
  const weekEnd = sunD.toISOString().slice(0, 10);
  const d15 = new Date(now);
  d15.setDate(d15.getDate() + 15);
  const d30 = new Date(now);
  d30.setDate(d30.getDate() + 30);
  const [mon1, monLast] = monthBounds(now);

  // C.2 — the six financial-tab builders (By Case / By Collaborator /
  // By Period / By Client / Collaborators / Invoices) moved server-side
  // (POST /api/financials/reports; same arithmetic, verbatim,
  // recording-verified: recordings/c2-reports/). The view posts the rows it
  // holds and displays the returned rows — it computes none of the money.
  // Latest-wins: a stale response never overwrites a newer one; the
  // previous result stays on screen until the next response lands (empty
  // tables before the first). The By-Collaborator period window travels
  // WITH the request (collabFrom/collabTo), still derived from `now`
  // client-side. Search/sort/status filters and the footer sums over the
  // FILTERED subset keep operating client-side on the server-handed rows.
  /* 174 §3d [RULED 2026-09-08, Laurent] — THE NINE HERE TOO, and through the
     one module. This offered three of them and computed the month window as
     `thisMonth + "-01"`, where `thisMonth` is a slice of `toISOString()` while
     its partner `monLast` is a LOCAL date — so between 00:00 and 02:00 local
     the pair could name the previous month's first day and this month's last.
     That is the residue of the class 150 §3 removed from the rest of this
     block; `periodBounds` ends it.

     THE BRIEF'S §3.3 SAID THIS VIEW "keeps … its own defective arithmetic" and
     that repointing it would fix a visible off-by-one. MEASURED: it does not.
     150 §3 already repointed `monthBounds`/`quarterBounds`/`yearBounds` here —
     the comment above says so in its own words — and the scheduling note in
     `src/lib/periodRange.ts`'s header, which said ReportsView "still carries
     its own copy with the defect", is what is stale. It is corrected there in
     this same commit.

     `null` for the axis: the only kind that reads it is "workweek", which no
     picker offers. 198 §C — no longer: the two accounting-year kinds read the
     firm's start off the settings object this view already holds (D.3), so
     it is what the seam carries now. */
  const collabKind = gf("collab") as string;
  const collabPeriodRange: [string | null, string | null] =
    collabKind === "all"
      ? [null, null]
      : (periodBounds(
          collabKind === "free"
            ? { kind: "free", from: String(filt.collabFrom ?? ""), to: String(filt.collabTo ?? "") }
            : { kind: collabKind as PeriodKind },
          firmSettings,
          now,
        ) ?? [null, null]);
  /* 139 §3 — THE PROSPECT AXIS FOR THIS VIEW, and the one distinction the
     whole pass turns on: WHICH MATTERS COUNT is not the same question as
     WHICH MATTERS CAN BE NAMED.

     `caPop`/`ccPop` are the POPULATION — the rows this screen totals, counts
     and reports on. They are what goes to the server aggregate below and what
     the party tables count. `ca`/`cc` themselves stay whole wherever the view
     merely RESOLVES an id to a ref and a title (the agenda rows' `ca.find`,
     the time-entry rows', the row-click's `nav`), because dropping a matter
     from a lookup does not hide it — it renders its row unnamed, which is a
     defect, not a filter.

     Default "both", so an untouched Reports screen reports the same book. */
  const [repProspect, setRepProspect] = useState<ProspectFilterValue>("both");
  const caPop = useMemo(
    () => (ca || []).filter((c: ProspectTaggable) => matterPassesProspect(c, repProspect)),
    [ca, repProspect],
  );
  const ccPop = useMemo(
    () => (cc || []).filter((c: ProspectTaggable) => matterPassesProspect(c, repProspect)),
    [cc, repProspect],
  );
  const { rows: _roleRows } = useRoleLabels();
  const [repCalc, setRepCalc] = useState<any>(null);
  const _rcSeq = useRef(0);
  useEffect(() => {
    const seq = ++_rcSeq.current;
    computeReportsAggregates({
      cases: caPop.map(redRepMatter),
      collectiveCases: ccPop.map(redRepMatter),
      clients: (cl || []).map(redRepClient),
      collaborators: (co || []).map(redRepCo),
      entries: (te || []).map(redRepEntry),
      invoices: (inv || []).map(redRepInv),
      roleRows: redRoleRows(_roleRows),
      collabFrom: collabPeriodRange[0],
      collabTo: collabPeriodRange[1],
    })
      .then((r: any) => {
        if (_rcSeq.current !== seq) return;
        setRepCalc(r);
      })
      .catch(() => {
        if (_rcSeq.current === seq) setRepCalc(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caPop, ccPop, cl, co, te, inv, _roleRows, collabPeriodRange[0], collabPeriodRange[1]]);
  const byCaseData = repCalc?.byCase || [];
  const byCollabData = repCalc?.byCollab || [];
  const byPeriodData = repCalc?.byPeriod || [];
  const byClientData = repCalc?.byClient || [];

  // ============ CLIENTS LIST ============
  const clientsData = useMemo(
    () =>
      (cl || []).map((c: any) => {
        const cases = caPop.filter((cs: any) =>
          (cs.clIds || []).includes(c.id),
        ).length;
        const addr = [c.street, c.postalCode, c.city, c.country]
          .filter(Boolean)
          .join(", ");
        return {
          name: pname(c),
          type: c.type || "individual",
          email: c.email || "",
          phone: c.phone || "",
          address: addr,
          vat: c.vat || "",
          lang: storedClientLanguage(c) || "—", // 207C §6 — what is stored (it read `lang`, never sent); 209 §5 [RULED 18] — « — » when none (it read « fr »)
          cases,
        };
      }),
    [cl, caPop],
  );

  // ============ OPPONENTS LIST ============
  const opponentsData = useMemo(
    () =>
      (op || []).map((o: any) => {
        const cases = caPop.filter((cs: any) =>
          (cs.opIds || []).includes(o.id),
        ).length;
        return {
          name: pname(o),
          type: o.type || "",
          contact: o.contact || "",
          notes: o.notes || "",
          cases,
        };
      }),
    [op, caPop],
  );

  // ============ OPP. LAWYERS LIST ============
  const oppLawData = useMemo(
    () =>
      (ocu || []).map((o: any) => {
        const cases = caPop.filter((cs: any) =>
          (cs.ocuIds || []).includes(o.id),
        ).length;
        return {
          name: pname(o),
          firm: o.firm || "",
          email: o.email || "",
          phone: o.phone || "",
          bar: o.barAssociation || o.bar || "",
          cases,
        };
      }),
    [ocu, caPop],
  );

  // ============ EXPERTS LIST ============
  const expertsData = useMemo(
    () =>
      (ex || []).map((e: any) => {
        const cases = caPop.filter((cs: any) =>
          (cs.exIds || []).includes(e.id),
        ).length;
        return {
          name: pname(e),
          specialty: e.spec || e.specialty || "",
          email: e.email || "",
          phone: e.phone || "",
          cases,
        };
      }),
    [ex, caPop],
  );

  // ============ JURISDICTIONS LIST ============
  const juData = useMemo(
    () =>
      (ju || []).map((j: any) => {
        const cases = caPop.filter((cs: any) => cs.juId === j.id).length;
        const addr = [j.address, j.postalCode, j.city]
          .filter(Boolean)
          .join(", ");
        return {
          name: j.name || "",
          address: addr,
          tel: j.tel || "",
          email: j.email || "",
          cases,
        };
      }),
    [ju, caPop],
  );

  // ============ COLLABORATORS LIST ============
  const collabsData = repCalc?.collabs || [];

  // ============ AGENDA LIST ============
  const agendaData = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    return (ag || [])
      .filter((a: any) => a.date && a.date.slice(0, 10) >= todayStr)
      .sort((a: any, b: any) => (a.date || "").localeCompare(b.date || ""))
      .map((a: any) => {
        const cs = resolveMatter({ caseId: a.caId, ccId: a.ccId }, ca, cc); // 191 C.2b — its own expression, moved onto the one resolver
        const assigned = (a.assignedCollaborators || [])
          .map((id: any) => {
            const c = (co || []).find((x: any) => x.id === id);
            return c ? pname(c) : "";
          })
          .filter(Boolean)
          .join(", ");
        return {
          date: a.date?.slice(0, 10) || "",
          time: a.time || "",
          type: a.type || "",
          caseRef: cs?.ref || "",
          title: a.title || "",
          assigned,
          _id: a.id,
        };
      });
  }, [ag, ca, cc, co]);

  // ============ CORRESPONDENCE ============
  // Phase 13 — filing list of billed emails, driven entirely by the existing
  // TimeEntry trail (D6a — no separate tracking). A billed-email entry is
  // identified by emailGraphId + activityType CORR/EXCORR. Subject is the
  // leading portion of the description (before " — N words"). Time-entry
  // rows carry both canonical (emailGraphId/activityType/description) and
  // abbreviated (egid/...) keys depending on source — read both defensively.
  const correspondenceData = useMemo(() => {
    return (te || [])
      .map((e: any) => ({
        e,
        egid: e.emailGraphId ?? e.egid,
        aType: e.activityType ?? e.type,
        aDesc: String(e.description ?? e.desc ?? ""),
      }))
      .filter((x: any) => x.egid && (x.aType === "CORR" || x.aType === "EXCORR"))
      .map(({ e, aType, aDesc }: any) => {
        const caId = e.caId ?? e.caseId,
          ccId = e.ccId,
          coId = e.coId ?? e.collaboratorId;
        const caseKey = caId ? "ca:" + caId : ccId ? "cc:" + ccId : "";
        const cs = resolveMatter({ caseId: caId, ccId }, ca, cc); // 191 C.2b
        const c = (co || []).find((x: any) => x.id === coId);
        return {
          date: (e.date || "").slice(0, 10),
          dirCode: aType === "CORR" ? "out" : "in",
          dir: aType === "CORR" ? "OUT" : "IN",
          caseKey,
          caseRef: cs?.ref || "",
          coId: coId || null,
          collab: c ? pname(c) : "",
          sender: e.senderAddress || "",
          subject: aDesc.split(" — ")[0].trim(),
          _id: e.id,
        };
      })
      .sort((a: any, b: any) => (b.date || "").localeCompare(a.date || ""));
  }, [te, ca, cc, co]);
  const corrCaseOpts = useMemo(() => {
    const seen = new Map();
    for (const d of correspondenceData)
      if (d.caseKey && !seen.has(d.caseKey))
        seen.set(d.caseKey, d.caseRef || d.caseKey);
    return [...seen.entries()].sort((a, b) =>
      String(a[1]).localeCompare(String(b[1])),
    );
  }, [correspondenceData]);
  const corrCoOpts = useMemo(() => {
    const seen = new Map();
    for (const d of correspondenceData)
      if (d.coId && !seen.has(d.coId)) seen.set(d.coId, d.collab);
    return [...seen.entries()]
      .map(([id, name]) => [String(id), name || "—"])
      .sort((a, b) => a[1].localeCompare(b[1]));
  }, [correspondenceData]);

  // ============ INVOICES LIST ============
  const invoicesData = repCalc?.invoices || [];

  // ============ TAB CONFIG ============
  /* 191 F.0 [RULED, LAURENT, 2026-09-17] — PAR DOSSIER AND PAR CLIENT SHOW
     THE CASE LIST'S SEVEN: Non marqué · Non facturable · Facturable ·
     Facturé ┃ Envoyé · Payé · Dû — the Dossiers list's own figures, bar
     included, read off each row's served `recap` members (posted with the
     matter, echoed by reportsAggregates.js; the view computes none of it).
     The headers reuse the lists' keys (`cc.col.matter_*`), the tones the
     lists' (RECAP_MEMBERS, matterListCfg.tsx), the bar the strip's
     (BASIS_BAR_BORDER) as the ENVOYÉ column's left border, header, rows and
     the total alike. Heures and every non-money column stay; the old money
     columns are gone. A null member reads "—" (absent is never zero). */
  /* 191 fold-in 2 [RULED, LAURENT — F.0]: THE TWO TABS OBEY THE LISTS' COLUMN
     SWITCHES. A recap column whose `furniture.list.recap_<m>` switch is off
     for the viewer — a role's bit written false, or hidden firm-wide, the
     administrator included — is absent from both tabs, their sums row and the
     Excel / print export (they all read `tabCfg`): the members below are
     filtered through `listColumnShown(columnGateOf(usePermissions()))`, the
     gate the two lists read (matterListCfg.tsx), never a second spelling.
     Unwritten = shown (191 (1)). The bar rides Envoyé only while Envoyé is
     shown; with every member switched off the tabs keep their other
     columns. */
  const columnShown = listColumnShown(columnGateOf({ viewer, can }));
  const shownMembers = RECAP_MEMBERS.filter(({ m }) => columnShown(`recap_${m}`));
  const sevenHeaders = shownMembers.map(({ lk, l }) => tr(lk, { fallback: l }));
  const sevenCells = (d: any) =>
    shownMembers.map(({ m }) => (d[m] == null ? "—" : moneyStack(d[m], foreignOf(d, m))));
  const sevenSortKeys = (from: number) =>
    Object.fromEntries(shownMembers.map(({ m }, i) => [from + i, m]));
  const sevenSums = (from: number) =>
    Object.fromEntries(
      shownMembers.map(({ m }, i) => [
        from + i,
        (d: any[]) =>
          d.every((r) => r[m] == null)
            ? "—"
            : moneyStack(d.reduce((s: number, r: any) => s + (r[m] ?? 0), 0), sumForeign(d, m)),
      ]),
    );
  const sevenColStyle = (from: number) =>
    Object.fromEntries(
      shownMembers.flatMap(({ bar }, i) => (bar ? [[from + i, { borderLeft: BASIS_BAR_BORDER }]] : [])),
    );
  const sevenCellStyle = (from: number) => (i: number, d: any) => {
    const mem = shownMembers[i - from];
    if (!mem || d[mem.m] == null) return mem ? { color: "var(--tm)" } : undefined;
    return { color: mem.m === "due" ? dueTone(d[mem.m]) : mem.tone };
  };
  const sevenRight = (from: number) => shownMembers.map((_, i) => from + i);
  const tabCfg = {
    case: {
      headers: [
        tr("reports.col.ref"),
        tr("reports.col.title"),
        tr("reports.col.type"),
        tr("reports.col.status"),
        tr("reports.col.hours"),
        ...sevenHeaders,
      ],
      data: byCaseData,
      row: (d: any) => [
        d.ref,
        d.title,
        resolveTaxonomyLabel(d.ctype, _caseTypeEntries),
        resolveTaxonomyLabel(d.status, _caseStatusEntries),
        fmin(d.totalMin),
        ...sevenCells(d),
      ],
      search: (d: any, ql: any) =>
        (
          d.ref +
          d.title +
          (resolveTaxonomyLabel(d.ctype, _caseTypeEntries) || "") +
          (resolveTaxonomyLabel(d.status, _caseStatusEntries) || "")
        )
          .toLowerCase()
          .includes(ql),
      filterFn: gf("case") === "all" ? null : (d: any) => d.status === gf("case"),
      filterUI: (
        <SegmentedControl {...({} as any)}
          value={gf("case")}
          options={[
            ["all", tr("common.all", { fallback: "All" })],
            [
              "active",
              resolveTaxonomyLabel("active", _caseStatusEntries) || "Active",
            ],
            [
              "pending",
              resolveTaxonomyLabel("pending", _caseStatusEntries) || "Suspended",
            ],
            [
              "closed",
              resolveTaxonomyLabel("closed", _caseStatusEntries) || "Closed",
            ],
            [
              "archived",
              resolveTaxonomyLabel("archived", _caseStatusEntries) ||
                "Archived",
            ],
          ]}
          onChange={(v: any) => sf("case", v)}
        />
      ),
      sortKeys: {
        0: "ref",
        1: "title",
        2: "ctype",
        3: "status",
        4: "totalMin",
        ...sevenSortKeys(5),
      },
      rightCols: [4, ...sevenRight(5)],
      sumCols: {
        4: (d: any) => fmin(d.reduce((s: any, r: any) => s + r.totalMin, 0)),
        ...sevenSums(5),
      },
      colStyle: sevenColStyle(5),
      cellStyle: sevenCellStyle(5),
    },
    collab: {
      headers: [
        tr("reports.col.collaborator"),
        tr("reports.col.role"),
        tr("reports.col.cases"),
        tr("reports.col.hours"),
        tr("reports.col.fees_exvat"),
        tr("reports.col.billed_hours"),
        tr("reports.col.unbilled_hours"),
      ],
      data: byCollabData,
      row: (d: any) => [
        d.name,
        d.role,
        d.cases,
        fmin(d.totalMin),
        eurN(d.totalFees),
        fmin(d.billedMin),
        fmin(d.unbilledMin),
      ],
      search: (d: any, ql: any) => (d.name + d.role).toLowerCase().includes(ql),
      /* 174 §3d — the nine, from `PERIOD_OPTIONS`. A ten-segment pill bar is
         not a control, so this one filter wears the house select (MenuSelect,
         151F §2 — the same face the time-entry period filter wears, offering
         the same list). "Free from/to…" opens the two dates beside it; every
         other kind computes its own window. */
      filterUI: (
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <MenuSelect
            ariaLabel={String(tr("te.filter.period", { fallback: "Period" }))}
            title={String(tr("te.filter.period", { fallback: "Period" }))}
            value={collabKind}
            onChange={(v) => {
              sf("collab", v);
            }}
            options={[
              ["all", String(tr("common.all"))],
              ...PERIOD_OPTIONS.map(
                (o) => [o.kind, String(tr(o.key, { ...o.vars, fallback: o.fallback }))] as [string, string], // 213B §4 — the quarters' `n`
              ),
            ]}
            style={{ height: 22, fontSize: FS.s10, minWidth: 130 }}
          />
          {collabKind === "free" && (
            <>
              <FreeDateInput
                className="field-input"
                style={{ height: 22, fontSize: FS.s10, width: 130 }}
                value={String(filt.collabFrom ?? "")}
                onCommit={(v) => {
                  sf("collabFrom", v);
                }}
              />
              <FreeDateInput
                className="field-input"
                style={{ height: 22, fontSize: FS.s10, width: 130 }}
                value={String(filt.collabTo ?? "")}
                onCommit={(v) => {
                  sf("collabTo", v);
                }}
              />
            </>
          )}
        </span>
      ),
      sortKeys: {
        0: "name",
        1: "role",
        2: "cases",
        3: "totalMin",
        4: "totalFees",
        5: "billedMin",
        6: "unbilledMin",
      },
      rightCols: [2, 3, 4, 5, 6],
      sumCols: {
        2: (d: any) => d.reduce((s: any, r: any) => s + r.cases, 0),
        3: (d: any) => fmin(d.reduce((s: any, r: any) => s + r.totalMin, 0)),
        4: (d: any) => eurN(d.reduce((s: any, r: any) => s + r.totalFees, 0)),
        5: (d: any) => fmin(d.reduce((s: any, r: any) => s + r.billedMin, 0)),
        6: (d: any) => fmin(d.reduce((s: any, r: any) => s + r.unbilledMin, 0)),
      },
    },
    period: {
      headers: [
        tr("reports.col.period"),
        tr("reports.col.entries"),
        tr("reports.col.hours"),
        tr("reports.col.fees_exvat"),
        tr("reports.col.billed_hours"),
        tr("reports.col.unbilled_hours"),
      ],
      data: byPeriodData,
      row: (d: any) => [
        d.label,
        d.entries,
        fmin(d.totalMin),
        eurN(d.totalFees),
        fmin(d.billedMin),
        fmin(d.unbilledMin),
      ],
      search: (d: any, ql: any) => d.label.toLowerCase().includes(ql),
      filterFn:
        gf("period") === "thisyear"
          ? (d: any) => d.period.startsWith(thisYear)
          : gf("period") === "lastyear"
            ? (d: any) => d.period.startsWith(lastYear)
            : null,
      filterUI: (
        <SegmentedControl {...({} as any)}
          value={gf("period")}
          options={[
            ["all", tr("common.all")],
            ["thisyear", tr("reports.period.this_year")],
            ["lastyear", tr("reports.period.last_year")],
          ]}
          onChange={(v: any) => sf("period", v)}
        />
      ),
      sortKeys: {
        0: "period",
        1: "entries",
        2: "totalMin",
        3: "totalFees",
        4: "billedMin",
        5: "unbilledMin",
      },
      rightCols: [1, 2, 3, 4, 5],
      sumCols: {
        1: (d: any) => d.reduce((s: any, r: any) => s + r.entries, 0),
        2: (d: any) => fmin(d.reduce((s: any, r: any) => s + r.totalMin, 0)),
        3: (d: any) => eurN(d.reduce((s: any, r: any) => s + r.totalFees, 0)),
        4: (d: any) => fmin(d.reduce((s: any, r: any) => s + r.billedMin, 0)),
        5: (d: any) => fmin(d.reduce((s: any, r: any) => s + r.unbilledMin, 0)),
      },
    },
    client: {
      headers: [
        tr("reports.col.client"),
        tr("reports.col.type"),
        tr("reports.col.cases"),
        tr("reports.col.hours"),
        ...sevenHeaders,
      ],
      data: byClientData,
      row: (d: any) => [
        d.name,
        d.type,
        d.cases,
        fmin(d.totalMin),
        ...sevenCells(d),
      ],
      search: (d: any, ql: any) => (d.name + d.type).toLowerCase().includes(ql),
      filterFn:
        gf("clientSt") === "all"
          ? null
          : (d: any) => {
              const cIds = caPop
                .filter(
                  (cs: any) =>
                    (cs.clIds || []).includes(d.clId) &&
                    cs.status === gf("clientSt"),
                )
                .map((cs: any) => cs.id);
              return cIds.length > 0;
            },
      filterUI: (
        <SegmentedControl {...({} as any)}
          value={gf("clientSt")}
          options={[
            ["all", tr("common.all", { fallback: "All" })],
            [
              "active",
              resolveTaxonomyLabel("active", _caseStatusEntries) || "Active",
            ],
            [
              "closed",
              resolveTaxonomyLabel("closed", _caseStatusEntries) || "Closed",
            ],
          ]}
          onChange={(v: any) => sf("clientSt", v)}
        />
      ),
      sortKeys: {
        0: "name",
        1: "type",
        2: "cases",
        3: "totalMin",
        ...sevenSortKeys(4),
      },
      rightCols: [2, 3, ...sevenRight(4)],
      sumCols: {
        2: (d: any) => d.reduce((s: any, r: any) => s + r.cases, 0),
        3: (d: any) => fmin(d.reduce((s: any, r: any) => s + r.totalMin, 0)),
        ...sevenSums(4),
      },
      colStyle: sevenColStyle(4),
      cellStyle: sevenCellStyle(4),
    },
    clients: {
      headers: [
        tr("reports.col.name"),
        tr("reports.col.type"),
        tr("reports.col.email"),
        tr("reports.col.phone"),
        tr("reports.col.address"),
        tr("reports.col.vat"),
        tr("reports.col.language"),
        tr("reports.col.cases"),
      ],
      data: clientsData,
      row: (d: any) => [
        d.name,
        d.type,
        d.email,
        d.phone,
        d.address,
        d.vat,
        d.lang,
        d.cases,
      ],
      search: (d: any, ql: any) =>
        (d.name + d.email + d.type + d.address).toLowerCase().includes(ql),
      sortKeys: { 0: "name", 1: "type", 2: "email", 6: "lang", 7: "cases" },
      rightCols: [7],
      sumCols: { 7: (d: any) => d.reduce((s: any, r: any) => s + r.cases, 0) },
    },
    opponents: {
      headers: [
        tr("reports.col.name"),
        tr("reports.col.type"),
        tr("reports.col.contact"),
        tr("reports.col.notes"),
        tr("reports.col.cases"),
      ],
      data: opponentsData,
      row: (d: any) => [d.name, d.type, d.contact, d.notes, d.cases],
      search: (d: any, ql: any) =>
        (d.name + d.type + d.contact).toLowerCase().includes(ql),
      sortKeys: { 0: "name", 1: "type", 4: "cases" },
      rightCols: [4],
      sumCols: { 4: (d: any) => d.reduce((s: any, r: any) => s + r.cases, 0) },
    },
    opplaw: {
      headers: [
        tr("reports.col.name"),
        tr("reports.col.firm"),
        tr("reports.col.email"),
        tr("reports.col.phone"),
        tr("reports.col.bar_association"),
        tr("reports.col.cases"),
      ],
      data: oppLawData,
      row: (d: any) => [d.name, d.firm, d.email, d.phone, d.bar, d.cases],
      search: (d: any, ql: any) =>
        (d.name + d.firm + d.email + d.bar).toLowerCase().includes(ql),
      sortKeys: { 0: "name", 1: "firm", 4: "bar", 5: "cases" },
      rightCols: [5],
      sumCols: { 5: (d: any) => d.reduce((s: any, r: any) => s + r.cases, 0) },
    },
    experts: {
      headers: [
        tr("reports.col.name"),
        tr("reports.col.specialty"),
        tr("reports.col.email"),
        tr("reports.col.phone"),
        tr("reports.col.cases"),
      ],
      data: expertsData,
      row: (d: any) => [d.name, d.specialty, d.email, d.phone, d.cases],
      search: (d: any, ql: any) =>
        (d.name + d.specialty + d.email).toLowerCase().includes(ql),
      sortKeys: { 0: "name", 1: "specialty", 4: "cases" },
      rightCols: [4],
      sumCols: { 4: (d: any) => d.reduce((s: any, r: any) => s + r.cases, 0) },
    },
    jurisdictions: {
      headers: [
        tr("reports.col.name"),
        tr("reports.col.address"),
        tr("reports.col.tel"),
        tr("reports.col.email"),
        tr("reports.col.cases"),
      ],
      data: juData,
      row: (d: any) => [d.name, d.address, d.tel, d.email, d.cases],
      search: (d: any, ql: any) =>
        (d.name + d.address + d.tel + d.email).toLowerCase().includes(ql),
      sortKeys: { 0: "name", 4: "cases" },
      rightCols: [4],
      sumCols: { 4: (d: any) => d.reduce((s: any, r: any) => s + r.cases, 0) },
    },
    collabs: {
      headers: [
        tr("reports.col.name"),
        tr("reports.col.role"),
        tr("reports.col.email"),
        tr("reports.col.total_hours"),
        tr("reports.col.total_fees"),
      ],
      data: collabsData,
      row: (d: any) => [
        d.name,
        d.role,
        d.email,
        fmin(d.totalMin),
        eurN(d.totalFees),
      ],
      search: (d: any, ql: any) => (d.name + d.role + d.email).toLowerCase().includes(ql),
      sortKeys: { 0: "name", 1: "role", 3: "totalMin", 4: "totalFees" },
      rightCols: [3, 4],
      sumCols: {
        3: (d: any) => fmin(d.reduce((s: any, r: any) => s + r.totalMin, 0)),
        4: (d: any) => eurN(d.reduce((s: any, r: any) => s + r.totalFees, 0)),
      },
    },
    agenda: {
      headers: [
        tr("reports.col.date"),
        tr("reports.col.time"),
        tr("reports.col.type"),
        tr("reports.col.case_ref"),
        tr("reports.col.title"),
        tr("reports.col.assigned"),
      ],
      data: agendaData,
      row: (d: any) => [fdE(d.date), d.time, d.type, d.caseRef, d.title, d.assigned],
      search: (d: any, ql: any) =>
        (d.title + d.caseRef + d.type + d.assigned).toLowerCase().includes(ql),
      filterFn:
        gf("agenda") === "week"
          ? (d: any) => d.date >= weekStart && d.date <= weekEnd
          : gf("agenda") === "15d"
            ? (d: any) =>
                d.date >= todayStr && d.date <= d15.toISOString().slice(0, 10)
            : gf("agenda") === "month"
              ? (d: any) => d.date >= mon1 && d.date <= monLast
              : gf("agenda") === "30d"
                ? (d: any) =>
                    d.date >= todayStr &&
                    d.date <= d30.toISOString().slice(0, 10)
                : null,
      filterUI: (
        <SegmentedControl {...({} as any)}
          value={gf("agenda")}
          options={[
            ["all", tr("common.all")],
            ["week", tr("agenda.range.this_week")],
            ["15d", tr("agenda.range.next_15")],
            ["month", tr("agenda.range.this_month")],
            ["30d", tr("agenda.range.next_30")],
          ]}
          onChange={(v: any) => sf("agenda", v)}
        />
      ),
      sortKeys: { 0: "date", 2: "type", 3: "caseRef" },
      rightCols: [],
      sumCols: null,
    },
    invoices: {
      headers: [
        tr("reports.col.number"),
        tr("reports.col.type"),
        tr("reports.col.date"),
        tr("reports.col.case_ref"),
        tr("reports.col.client"),
        tr("reports.col.fees"),
        tr("reports.col.total"),
        tr("reports.col.paid"),
        tr("reports.col.remaining"),
        tr("reports.col.status"),
      ],
      data: invoicesData,
      row: (d: any) => [
        d.number,
        d.invType,
        fdE(d.date),
        d.caseRef,
        d.client,
        eurN(d.fees),
        eurN(d.total),
        eurN(d.paid),
        eurN(d.remaining),
        // 209 §16 — a row is ONE document: its status in words (« Partiellement
        // payé »), never the code (« partially_paid »). The filter keeps its own
        // words, which name a set (209 §9).
        statusWord(d.status),
      ],
      // …and the search finds a row by the word it prints, as by its code.
      search: (d: { number: string; caseRef: string; client: string; status: string }, ql: string) =>
        (d.number + d.caseRef + d.client + d.status + statusWord(d.status)).toLowerCase().includes(ql),
      // Gate 4 C5 D11 β — two-axis filter (payment-status + Invoiced).
      // Combines additively: an invoice passes when both axes accept it.
      filterFn: (() => {
        const stFilt = gf("invSt");
        const invFilt = gf("invInvoiced");
        const stOk =
          !stFilt || stFilt === "all" ? () => true : (d: any) => d.status === stFilt;
        const invOk =
          invFilt === "invoiced"
            ? (d: any) => d.invoiced
            : invFilt === "not-invoiced"
              ? (d: any) => !d.invoiced
              : () => true;
        return (d: any) => stOk(d) && invOk(d);
      })(),
      filterUI: (
        <>
          <SegmentedControl {...({} as any)}
            value={gf("invSt")}
            options={[
              ["all", tr("common.all")],
              // 180 §6 — draft and ready STOOD HERE: no path reaches either
              // (179 C); the enum keeps them (a type rebuild), the filter does not.
              /* 209 §9 — A FILTER'S OPTIONS NAME A SET: the filter words
                 (« Impayées », « Payées » — the Invoices list's own, 208 §21),
                 never one document's status words (« Impayé », « Payé »).
                 [ADVISOR] its two other options had no filter word: given one,
                 so the whole filter speaks as one. */
              ["unpaid", tr("invoice.pf.unpaid", { fallback: "Unpaid" })],
              // 181 C.1 — the state the rows carry (reportsAggregates.js status)
              // and the filter never offered.
              ["partially_paid", tr("invoice.pf.partially_paid", { fallback: "Partially paid" })],
              ["paid", tr("invoice.pf.paid", { fallback: "Paid" })],
              ["cancelled", tr("invoice.pf.cancelled", { fallback: "Cancelled" })],
            ]}
            onChange={(v: any) => sf("invSt", v)}
          />
          <SegmentedControl {...({} as any)}
            value={gf("invInvoiced")}
            options={[
              ["all", tr("common.all")],
              ["invoiced", tr("invoice.billit.sent")],
              ["not-invoiced", tr("invoice.billit.not_sent")],
            ]}
            onChange={(v: any) => sf("invInvoiced", v)}
          />
        </>
      ),
      sortKeys: {
        0: "number",
        2: "date",
        3: "caseRef",
        4: "client",
        5: "fees",
        6: "total",
        7: "paid",
        8: "remaining",
        9: "status",
      },
      rightCols: [5, 6, 7, 8],
      sumCols: {
        5: (d: any) => eurN(d.reduce((s: any, r: any) => s + r.fees, 0)),
        6: (d: any) => eurN(d.reduce((s: any, r: any) => s + r.total, 0)),
        7: (d: any) => eurN(d.reduce((s: any, r: any) => s + r.paid, 0)),
        8: (d: any) => eurN(d.reduce((s: any, r: any) => s + r.remaining, 0)),
      },
    },
    correspondence: {
      headers: [
        tr("reports.col.date"),
        tr("reports.corr.col_direction", { fallback: "Direction" }),
        tr("reports.col.case_ref"),
        tr("reports.col.collaborator"),
        tr("reports.col.sender", { fallback: "Sender" }),
        tr("reports.col.subject", { fallback: "Subject" }),
      ],
      data: correspondenceData,
      row: (d: any) => [
        fdE(d.date),
        d.dir,
        d.caseRef,
        d.collab,
        d.sender || "—",
        d.subject,
      ],
      search: (d: any, ql: any) =>
        (d.caseRef + d.collab + d.sender + d.subject + d.dir)
          .toLowerCase()
          .includes(ql),
      filterFn: (() => {
        const dir = gf("corrDir");
        const csk = gf("corrCase");
        const cok = gf("corrCo");
        return (d: any) =>
          (dir === "all" || d.dirCode === dir) &&
          (csk === "all" || d.caseKey === csk) &&
          (cok === "all" || String(d.coId) === cok);
      })(),
      filterUI: (
        <>
          <SegmentedControl {...({} as any)}
            value={gf("corrDir")}
            options={[
              ["all", tr("common.all", { fallback: "All" })],
              ["in", tr("reports.corr.in", { fallback: "IN" })],
              ["out", tr("reports.corr.out", { fallback: "OUT" })],
            ]}
            onChange={(v: any) => sf("corrDir", v)}
          />
          <SegmentedControl {...({} as any)}
            value={gf("corrCase")}
            options={[
              ["all", tr("reports.corr.all_cases", { fallback: "All cases" })],
              ...corrCaseOpts,
            ]}
            onChange={(v: any) => sf("corrCase", v)}
          />
          <SegmentedControl {...({} as any)}
            value={gf("corrCo")}
            options={[
              [
                "all",
                tr("reports.corr.all_collabs", {
                  fallback: "All collaborators",
                }),
              ],
              ...corrCoOpts,
            ]}
            onChange={(v: any) => sf("corrCo", v)}
          />
        </>
      ),
      sortKeys: {
        0: "date",
        1: "dir",
        2: "caseRef",
        3: "collab",
        4: "sender",
        5: "subject",
      },
      rightCols: [],
      sumCols: {},
    },
    // Phase 3 C5 — six CDD report views consume the aggregated payload at
    // cddReport (fetched once on tab entry). Empty data while loading is
    // tolerated by the generic table renderer.
    cddHealth: {
      headers: ["Row category", "Total cases", "Complete-or-NA", "% complete"],
      data: cddReport?.health || [],
      row: (d: any) => [d.key, d.total, d.ok, d.pct + "%"],
      search: (d: any, ql: any) => (d.key || "").toLowerCase().includes(ql),
      filterUI: null,
      sortKeys: { 0: "key", 1: "total", 2: "ok", 3: "pct" },
      rightCols: [1, 2, 3],
    },
    cddTier: {
      headers: ["Tier", "Cases"],
      data: cddReport?.tierDist || [],
      row: (d: any) => [d.tier, d.count],
      search: (d: any, ql: any) => (d.tier || "").toLowerCase().includes(ql),
      filterUI: null,
      sortKeys: { 0: "tier", 1: "count" },
      rightCols: [1],
    },
    cddExpiring: {
      headers: ["Kind", "Ref / name", "Due"],
      data: cddReport?.expiring || [],
      row: (d: any) => [d.kind, d.ref || d.name || "—", fdE(d.dueAt)],
      search: (d: any, ql: any) =>
        ((d.kind || "") + (d.ref || "") + (d.name || ""))
          .toLowerCase()
          .includes(ql),
      filterUI: null,
      sortKeys: { 0: "kind", 1: "ref", 2: "dueAt" },
      rightCols: [],
    },
    cddPep: {
      headers: ["Client", "Screened", "Notes"],
      data: cddReport?.pepRegister || [],
      row: (d: any) => [d.name || "—", fdE(d.pepScreenedAt), d.pepNotes || "—"],
      search: (d: any, ql: any) =>
        ((d.name || "") + (d.pepNotes || "")).toLowerCase().includes(ql),
      filterUI: null,
      sortKeys: { 0: "name", 1: "pepScreenedAt" },
      rightCols: [],
    },
    cddSar: {
      headers: ["Ref", "Title", "Status", "Filed", "RP"],
      data: cddReport?.sarLog || [],
      row: (d: any) => [
        d.ref || "—",
        d.title || "—",
        d.sarStatus,
        fdE(d.sarFiledAt),
        d.rp || "—",
      ],
      search: (d: any, ql: any) =>
        ((d.ref || "") + (d.title || "") + (d.rp || ""))
          .toLowerCase()
          .includes(ql),
      filterUI: null,
      sortKeys: { 0: "ref", 1: "title", 2: "sarStatus", 3: "sarFiledAt" },
      rightCols: [],
    },
    cddAtypical: {
      headers: ["Ref", "Title", "Type", "Amount", "Date", "Status"],
      data: cddReport?.atypicalLog || [],
      row: (d: any) => [
        d.ref || "—",
        d.title || "—",
        d.type || "—",
        d.amount != null ? eurN(d.amount) : "—",
        fdE(d.occurredAt),
        d.status,
      ],
      search: (d: any, ql: any) =>
        ((d.ref || "") + (d.title || "") + (d.type || ""))
          .toLowerCase()
          .includes(ql),
      filterUI: null,
      sortKeys: {
        0: "ref",
        1: "title",
        2: "type",
        3: "amount",
        4: "occurredAt",
        5: "status",
      },
      rightCols: [3],
    },
  };

  // No tabs visible — viewer has neither reportsFinancial nor reportsOperational.
  // (Should not happen in normal flow because App.jsx nav already filters; fail
  // gracefully if a deep-link lands here.)
  if (visibleTabs.length === 0) {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          color: "var(--tm)",
          fontSize: FS.s12,
        }}
      >
        {tr("reports.no_access_role")}
      </div>
    );
  }
  // Permissions tab — custom data source, bypass the table-driven render.
  // 182 B.3 — the funnel rides the permissions tab's stand-alone shape: the tab
  // bar, then a panel component of its own.
  if (tab === "permissions" || tab === "funnel") {
    return (
      <>
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          {visibleTabs.map((t) => {
            const active = tab === t.k;
            const Ic = t.i;
            return (
              /* 188 (7) — A TILE, left deliberately: a 70px column of a 24px
                 icon over a s9 label, r8, translucent when off. SegmentedControl
                 is a joined pill and this is not one; S3c's button pass takes
                 these as buttons. No later pass reads "tab" here and converts
                 it. Measured in docs/188-switcher-strips.md. */
              <button
                key={t.k}
                style={{
                  width: 70,
                  padding: "10px 4px 8px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  cursor: "pointer",
                  borderRadius: RAD.r8,
                  border: active
                    ? "1px solid rgba(74,124,255,.4)"
                    : "1px solid var(--br)",
                  background: active ? "var(--as)" : "rgba(255,255,255,.03)",
                  color: active ? "var(--ac)" : "var(--tm)",
                  fontFamily: "inherit",
                  transition: "all .12s",
                }}
                onClick={() => {
                  setTab(t.k);
                  setQ("");
                  setSort({ k: null, d: 1 });
                }}
              >
                <Ic size={24} strokeWidth={1.5} />
                <span
                  style={{
                    fontSize: FS.s9,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    textAlign: "center",
                  }}
                >
                  {tr(t.labelKey, { fallback: t.l })}
                </span>
              </button>
            );
          })}
        </div>
        {tab === "funnel" ? <FunnelReportPanel /> : <PermissionsReportPanel />}
      </>
    );
  }
  const cfg: any = tabCfg[tab as keyof typeof tabCfg];
  if (!cfg) return null;
  // Defensive: if tab is somehow set to a hidden one, render nothing
  // (the useEffect above will snap to a visible tab on the next render).
  const activeTabMeta = TABS.find((t) => t.k === tab);
  if (activeTabMeta && !visibleTabs.some((t) => t.k === activeTabMeta.k))
    return null;

  // Apply tab-specific filter, then search
  const preFiltered = cfg.filterFn ? cfg.data.filter(cfg.filterFn) : cfg.data;
  const ql = q.toLowerCase();
  const filtered = ql
    ? preFiltered.filter((d: any) => cfg.search(d, ql))
    : preFiltered;

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    if (!sort.k) return 0;
    const va = a[sort.k];
    const vb = b[sort.k];
    if (typeof va === "number" && typeof vb === "number")
      return sort.d * (va - vb);
    return sort.d * String(va || "").localeCompare(String(vb || ""));
  });

  const cycleSort = (colIdx: any) => {
    const k = cfg.sortKeys[colIdx];
    if (!k) return;
    if (sort.k === k) setSort({ k, d: sort.d === 1 ? -1 : 1 });
    else setSort({ k, d: 1 });
  };
  const sortInd = (colIdx: any) => {
    const k = cfg.sortKeys[colIdx];
    if (!k) return null;
    return <SortCaret active={sort.k === k} d={sort.d} />; // 200-glance §M — the one sort caret
  };

  // Export data
  const _activeTab = TABS.find((t) => t.k === tab);
  const tabLabel = _activeTab
    ? tr(_activeTab.labelKey, { fallback: _activeTab.l })
    : tab;
  const exportTitle = `${tr("reports.export_title", { fallback: "Report" })} — ${tabLabel}`;
  const allRows = sorted.map((d) => cfg.row(d));
  // C.2 — server rows not yet landed for a money tab: hold the exports.
  const _moneyPending = MONEY_TAB_KEYS.has(tab) && repCalc == null;
  // 203-glance §C — the dress every export of this screen wears: the day theme, the tab's figure columns
  const exportDress: ExportDress = { theme: dayTheme, font: appFont, rightCols: cfg.rightCols };

  // Slice 04e — the cells are RECORD VALUES (`cfg.row(d)` returns client names,
  // case refs and titles straight from the row: see cddPep/cddSar above), and
  // this document is written into a same-origin iframe. Every interpolated value
  // is escaped; the `<th>/<td>` scaffolding around them is not. The class
  // attribute is a literal chosen from rightCols, not data.
  /* 203-glance-2 §D [RULED — Laurent, 2026-09-21: "add the totals"] — ONE TOTALS
     ROW FOR THE THREE OUTPUTS. doPdf and doWord each built their own (the same
     label, the same `sumCols`, one with `null` and one with `""` where a column
     sums nothing) and doExcel passed none — so Reports' Excel lacked the row its
     Word and print carried. The row is built ONCE, in the shape the exporters
     share (the label first, a figure per summed column, `null` where a column
     sums nothing: print leaves the cell empty, Word writes "", Excel leaves the
     cell ABSENT — an empty-string cell would block Excel's overflow, 203-glance
     §D), and every output takes it: the Excel from the same code as the four
     Costs & Fees tables (b1f83b48), never a second implementation. `null` for a
     tab with no `sumCols` — no totals row anywhere, as before. */
  const totalLabel = tr("reports.total_n", {
    count: sorted.length,
    fallback: `Total (${sorted.length})`,
  });
  const exportTotalRow: (string | null)[] | null = cfg.sumCols
    ? cfg.headers.map((_: any, i: any) =>
        i === 0 ? totalLabel : cfg.sumCols[i] ? cfg.sumCols[i](sorted) : null,
      )
    : null;

  // 203 §A.2.0 — the markup is printTable's (one print path with Costs & Fees)
  const doPdf = () => {
    printTable(exportTitle, cfg.headers, allRows, exportTotalRow, firmName, exportDress);
  };

  const doWord = () => {
    // 203-glance §C — the total row travels as its own bold row now, no longer appended to the data
    void exportWord(exportTitle, cfg.headers, allRows, firmName, exportTotalRow, exportDress);
  };

  const doExcel = () => {
    void exportExcel(exportTitle, cfg.headers, allRows, exportTotalRow, exportDress); // 203-glance-2 §D — the totals row, last and bold, as in the four Costs & Fees Excels
  };

  return (
    <>
      {/* Tab bar — only visible (gate-passing) tabs */}
      <div
        style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}
      >
        {visibleTabs.map((t) => {
          const active = tab === t.k;
          const Ic = t.i;
          return (
            /* 188 (7) — A TILE WITH HOVER, left deliberately: the tile above
               plus onMouseEnter/onMouseLeave repainting the background when
               off. SegmentedControl is a joined pill and this is not one;
               S3c's button pass takes these as buttons. No later pass reads
               "tab" here and converts it. Measured in docs/188-switcher-strips.md. */
            <button
              key={t.k}
              style={{
                width: 70,
                padding: "10px 4px 8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                cursor: "pointer",
                borderRadius: RAD.r8,
                border: active
                  ? "1px solid rgba(74,124,255,.4)"
                  : "1px solid var(--br)",
                background: active ? "var(--as)" : "rgba(255,255,255,.03)",
                color: active ? "var(--ac)" : "var(--tm)",
                fontFamily: "inherit",
                transition: "all .12s",
              }}
              onClick={() => {
                setTab(t.k);
                setQ("");
                setSort({ k: null, d: 1 });
              }}
              onMouseEnter={(e) => {
                if (!active)
                  e.currentTarget.style.background = "rgba(255,255,255,.06)";
              }}
              onMouseLeave={(e) => {
                if (!active)
                  e.currentTarget.style.background = "rgba(255,255,255,.03)";
              }}
            >
              <Ic size={24} strokeWidth={1.5} />
              <span
                style={{
                  fontSize: FS.s9,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  textAlign: "center",
                }}
              >
                {tr(t.labelKey, { fallback: t.l })}
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <div className="search-box" style={{ width: 200 }}>
          <Search size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
          <input
            placeholder={tr("common.search", { fallback: "Search…" })}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {cfg.filterUI}
        {/* 139 §3 — the prospect axis, in the SHARED toolbar rather than in a
            tab's own `filterUI`, because it selects the population every
            matter-derived report on this screen reads, not one report's rows.
            A PeriodFilter, which is what every filterUI beside it already is.

            SHOWN ONLY WHERE IT DOES SOMETHING. Two tab families on this screen
            report on nothing that has an `isProspect` to test: the six CDD
            reports (`gate: "cdd"`, server-fetched from CDD records) and
            Permissions (grants, fetched by usePermissions). Offering a matter
            filter above either would be a control that moves no number — so it
            is gated off both, by the tab meta the file already carries rather
            than by a new flag on nineteen configs. */}
        {_activeTab && _activeTab.gate !== "cdd" && tab !== "permissions" ? (
          <SegmentedControl
            value={repProspect}
            options={prospectFilterOptions(tr)}
            onChange={(v: ProspectFilterValue) => setRepProspect(v)}
            counts={prospectFilterCounts([...(ca || []), ...(cc || [])])}
          />
        ) : null}
        <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
          {_moneyPending
            ? "…"
            : tr("reports.rows_count", {
                count: sorted.length,
                fallback: `${sorted.length} row${sorted.length !== 1 ? "s" : ""}`,
              })}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          <button
            className="btn btn-ghost"
            style={{ padding: "3px 8px", fontSize: FS.s10, opacity: _moneyPending ? 0.5 : 1 }}
            disabled={_moneyPending}
            onClick={doPdf}
            title={tr("reports.export_pdf", { fallback: "Export PDF" })}
          >
            <FileText
              size={12}
              style={{ marginRight: 3, verticalAlign: "middle" }}
            />
            {tr("reports.pdf", { fallback: "PDF" })}
          </button>
          <button
            className="btn btn-ghost"
            style={{ padding: "3px 8px", fontSize: FS.s10, opacity: _moneyPending ? 0.5 : 1 }}
            disabled={_moneyPending}
            onClick={doWord}
            title={tr("reports.export_word", { fallback: "Export Word" })}
          >
            <FileType
              size={12}
              style={{ marginRight: 3, verticalAlign: "middle" }}
            />
            {tr("reports.word", { fallback: "Word" })}
          </button>
          <button
            className="btn btn-ghost"
            style={{ padding: "3px 8px", fontSize: FS.s10, opacity: _moneyPending ? 0.5 : 1 }}
            disabled={_moneyPending}
            onClick={doExcel}
            title={tr("reports.export_excel", { fallback: "Export Excel" })}
          >
            <Sheet
              size={12}
              style={{ marginRight: 3, verticalAlign: "middle" }}
            />
            {tr("reports.excel", { fallback: "Excel" })}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrap">
        <Table style={{ width: "100%" }}>
          <thead>
            <tr>
              {cfg.headers.map((h: any, i: any) => (
                <th
                  key={i}
                  style={{
                    fontSize: FS.s9,
                    padding: "5px 6px",
                    color: "var(--tm)",
                    cursor: cfg.sortKeys[i] ? "pointer" : "default",
                    userSelect: "none",
                    textAlign: cfg.rightCols.includes(i) ? "right" : "left",
                    whiteSpace: "nowrap",
                    /* 191 F.0 — a column's own style (the bar on ENVOYÉ), header,
                       rows and the total alike. */
                    ...(cfg.colStyle?.[i] ?? {}),
                  }}
                  onClick={() => cycleSort(i)}
                >
                  {h}
                  {sortInd(i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td
                  colSpan={cfg.headers.length}
                  style={{
                    textAlign: "center",
                    padding: 20,
                    fontSize: FS.s11,
                    color: "var(--tm)",
                  }}
                >
                  {q
                    ? tr("reports.no_data_matching", {
                        q,
                        fallback: `No data matching "${q}"`,
                      })
                    : tr("reports.no_data", { fallback: "No data" })}
                </td>
              </tr>
            )}
            {sorted.map((d, idx) => {
              const row = cfg.row(d);
              return (
                <tr key={idx}>
                  {row.map((cell: any, i: any) => (
                    <td
                      key={i}
                      style={{
                        fontSize: FS.s10,
                        padding: "5px 6px",
                        textAlign: cfg.rightCols.includes(i) ? "right" : "left",
                        fontFamily: cfg.rightCols.includes(i)
                          ? "'JetBrains Mono',monospace"
                          : "inherit",
                        whiteSpace: "nowrap",
                        ...(i === 0 && tab === "case"
                          ? {
                              color: "var(--ac)",
                              cursor: "pointer",
                              fontFamily: "'JetBrains Mono',monospace",
                            }
                          : {}),
                        ...(cfg.colStyle?.[i] ?? {}),
                        /* 191 F.0 — the seven's tones, the lists' own. */
                        ...(cfg.cellStyle ? (cfg.cellStyle(i, d) ?? {}) : {}),
                      }}
                      onClick={
                        i === 0 && tab === "case" && nav
                          ? () => nav(d._isCC ? "cc" : "ca", d._id)
                          : undefined
                      }
                    >
                      {renderMoney(cell)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          {sorted.length > 0 && cfg.sumCols && (
            <tfoot>
              <tr style={{ borderTop: "2px solid var(--br)" }}>
                {cfg.headers.map((_: any, i: any) => (
                  <td
                    key={i}
                    style={{
                      fontSize: FS.s10,
                      padding: "6px",
                      fontWeight: 600,
                      textAlign: cfg.rightCols.includes(i) ? "right" : "left",
                      fontFamily: cfg.rightCols.includes(i)
                        ? "'JetBrains Mono',monospace"
                        : "inherit",
                      ...(cfg.colStyle?.[i] ?? {}),
                    }}
                  >
                    {i === 0
                      ? tr("reports.total_n", {
                          count: sorted.length,
                          fallback: `Total (${sorted.length})`,
                        })
                      : cfg.sumCols[i]
                        ? renderMoney(cfg.sumCols[i](sorted))
                        : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </Table>
      </div>
    </>
  );
};

export default ReportsView;
